import React from 'react'
import styled from 'styled-components'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import { cardStyle, font, ink, line, radius, shadow, surface, transition } from 'features/dashboard/tokens'
import { scoreColour, scoreBand, scoreBandFor, formatPercent, RAG } from 'features/dashboard/charts/scoreColour'
import { ENTITY_COLORS } from 'constants/entityColors'

const VARIANT_ACCENT = {
  ...ENTITY_COLORS,
  ungrouped: ink.mute,
}

const TYPE_LABEL = {
  portfolio: 'Portfolio',
  estate: 'Estate',
  bp: 'Building Passport',
  ungrouped: 'Loose buildings',
}

const warnTitle = count =>
  `${count} unmet statutory obligation${count === 1 ? '' : 's'} — legally required by legislation, with no evidence on file.`

const CLEAR_TITLE =
  'Every statutory obligation here has current evidence on file — nothing overdue or missing.'
const EMPTY_TITLE =
  'This group has no Building Passports yet, so there are no statutory obligations to meet.'
const NOT_SCORED_TITLE =
  'Compliance not scored yet — these Building Passports haven’t finished setup, so there isn’t enough on file to calculate a score. Any unmet statutory duties are still shown below.'
const meterTitle = score =>
  `Compliance score ${formatPercent(score)} (${scoreBand(score)}) — the share of weighted statutory obligations with current evidence on file.`

const EntityCard = ({
  variant = 'portfolio',
  name,
  type,
  score = null,
  unmetStatutory = 0,
  stats,
  childSummary,
  unitCount,
  onOpen,
  showCompliance = true,
  dataTourId,
}) => {
  const accent = VARIANT_ACCENT[variant] || VARIANT_ACCENT.portfolio
  const typeLabel = type || TYPE_LABEL[variant] || 'Portfolio'
  const hasScore = score !== null && score !== undefined
  const hasBuildings = variant === 'bp' ? true : (unitCount ?? 0) > 0
  const meterColour = hasScore ? scoreColour(score) : null

  const stripe = !showCompliance
    ? accent
    : hasScore
      ? scoreColour(score)
      : unmetStatutory > 0
        ? RAG.critical.fg
        : hasBuildings
          ? RAG.low.fg
          : line.strong

  const open = () => onOpen && onOpen()

  return (
    <Card
      role='button'
      tabIndex={0}
      $accent={accent}
      data-tour-id={dataTourId}
      onClick={open}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          open()
        }
      }}
    >
      <Stripe $c={stripe} aria-hidden='true' />

      <Top>
        <Glyph $accent={accent} $dashed={variant === 'ungrouped'} aria-hidden='true'>
          {name ? name.charAt(0).toUpperCase() : '—'}
        </Glyph>
        <Id>
          <Type>{typeLabel}</Type>
          <Name title={name}>{name}</Name>
        </Id>
        <Open className='open'>
          Open <ArrowForwardRoundedIcon style={{ fontSize: 15 }} />
        </Open>
      </Top>

      {stats && stats.length ? (
        <Stats>
          {stats.map(([value, label], i) => (
            <Pill key={i}>{label ? <><b>{value}</b> {label}</> : value}</Pill>
          ))}
        </Stats>
      ) : childSummary ? (
        <Meta>{childSummary}</Meta>
      ) : null}

      {showCompliance && (hasScore || hasBuildings) && (
        <Meter
          title={hasScore ? meterTitle(score) : NOT_SCORED_TITLE}
          aria-label={hasScore ? meterTitle(score) : NOT_SCORED_TITLE}
        >
          <MeterTop>
            <Pct $c={hasScore ? meterColour : ink.mute}>{hasScore ? formatPercent(score) : '—'}</Pct>
            <MeterLabel>{hasScore ? `obligations met · ${scoreBand(score)}` : 'compliance not scored yet'}</MeterLabel>
          </MeterTop>
          <Track $empty={!hasScore}>
            {hasScore && <Fill $c={meterColour} style={{ width: `${Math.max(2, Math.min(100, score))}%` }} />}
          </Track>
        </Meter>
      )}

      {showCompliance && (
        <Foot>
          {unmetStatutory > 0 ? (
            <Warning title={warnTitle(unmetStatutory)} aria-label={warnTitle(unmetStatutory)}>
              <WarnGlyph aria-hidden='true'>
                <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'>
                  <path d='M10.3 3.9 1.8 18a1.5 1.5 0 0 0 1.3 2.2h17.8a1.5 1.5 0 0 0 1.3-2.2L13.7 3.9a1.5 1.5 0 0 0-2.6 0z' />
                  <path d='M12 9v4M12 17h.01' />
                </svg>
              </WarnGlyph>
              {unmetStatutory} unmet statutory obligation{unmetStatutory === 1 ? '' : 's'}
            </Warning>
          ) : hasBuildings ? (
            <Status $tone='good' title={CLEAR_TITLE} aria-label={CLEAR_TITLE}>
              <StatusGlyph aria-hidden='true'>
                <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.6' strokeLinecap='round' strokeLinejoin='round'>
                  <path d='M20 6 9 17l-5-5' />
                </svg>
              </StatusGlyph>
              No unmet statutory obligations
            </Status>
          ) : (
            <Status $tone='mute' title={EMPTY_TITLE} aria-label={EMPTY_TITLE}>
              No Building Passports yet
            </Status>
          )}
        </Foot>
      )}
    </Card>
  )
}

export default EntityCard

const Card = styled.div`
  ${cardStyle}
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 13px;
  padding: 17px 18px 17px 21px;
  min-height: 104px;
  cursor: pointer;
  overflow: hidden;
  transition: ${transition};

  &:hover {
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: ${shadow.lift};
  }
  &:hover .open {
    color: ${({ theme }) => theme.colors.secondary};
    gap: 7px;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 2px;
  }
`

const Stripe = styled.span`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  background: ${({ $c }) => $c};
`

const Top = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
`

const Glyph = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: ${radius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${font.emphasis};
  font-weight: 700;
  background: ${({ $accent, $dashed }) => ($dashed ? 'transparent' : `${$accent}14`)};
  color: ${({ $accent }) => $accent};
  ${({ $dashed }) => $dashed && `border: 1.5px dashed currentColor; opacity: 0.7;`}
`

const Id = styled.div`
  flex: 1;
  min-width: 0;
`

const Type = styled.div`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${ink.mute};
`

const Name = styled.div`
  font-size: ${font.emphasis};
  font-weight: 700;
  color: ${ink.base};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 1px;
`

const Open = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${font.caption};
  font-weight: 700;
  color: ${ink.mute};
  transition: ${transition};
`

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
`

const Pill = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  font-size: ${font.caption};
  font-weight: 600;
  color: ${ink.soft};
  background: ${surface.wash};
  border: 1px solid ${line.base};
  border-radius: ${radius.pill};
  padding: 4px 11px;
  b {
    font-weight: 700;
    color: ${ink.base};
    font-variant-numeric: tabular-nums;
  }
`

const Meta = styled.div`
  font-size: ${font.small};
  color: ${ink.mute};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Meter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: help;
`

const MeterTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`

const Pct = styled.span`
  font-size: ${font.emphasis};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ $c }) => $c};
`

const MeterLabel = styled.span`
  font-size: ${font.caption};
  color: ${ink.mute};
  text-transform: capitalize;
`

const Track = styled.div`
  height: 7px;
  border-radius: ${radius.pill};
  background: ${surface.wash};
  border: 1px solid ${line.base};
  overflow: hidden;
  ${({ $empty }) => $empty && `
    background-image: repeating-linear-gradient(
      45deg,
      ${line.base} 0,
      ${line.base} 4px,
      transparent 4px,
      transparent 8px
    );
  `}
`

const Fill = styled.div`
  height: 100%;
  border-radius: ${radius.pill};
  background: ${({ $c }) => $c};
  transition: width 0.4s ease;
`

const Foot = styled.div`
  margin-top: auto;
  padding-top: 13px;
  border-top: 1px solid ${line.base};
`

const Warning = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 11px;
  border-radius: ${radius.pill};
  background: ${RAG.critical.bg};
  border: 1px solid ${RAG.critical.fg}33;
  font-size: ${font.caption};
  font-weight: 700;
  color: ${RAG.critical.fg};
  cursor: help;
`

const WarnGlyph = styled.span`
  display: inline-flex;
  line-height: 0;
`

const Status = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 11px;
  border-radius: ${radius.pill};
  font-size: ${font.caption};
  font-weight: 700;
  cursor: help;
  background: ${({ $tone }) => ($tone === 'good' ? RAG.low.bg : surface.wash)};
  border: 1px solid ${({ $tone }) => ($tone === 'good' ? `${RAG.low.fg}33` : line.base)};
  color: ${({ $tone }) => ($tone === 'good' ? RAG.low.fg : ink.mute)};
`

const StatusGlyph = styled.span`
  display: inline-flex;
  line-height: 0;
`
